enum HTTPMethod {
  GET = 'GET',
  POST = 'POST',
  PUT = 'PUT',
  DELETE = 'DELETE',
}

interface RequestStructure {
  method: HTTPMethod
  path: string
  headers?: Record<string, string>
  body?: string | object
}

class ProviderAPIClient {
  private static baseURL: string = import.meta.env.VITE_PROVIDER_API_URL
  private apiKey?: string
  private static instance: ProviderAPIClient | null = null

  private constructor(apiKey?: string) {
    this.apiKey = apiKey
  }

  static getInstance(apiKey?: string): ProviderAPIClient {
    if (!ProviderAPIClient.instance) {
      ProviderAPIClient.instance = new ProviderAPIClient(apiKey)
    }
    return ProviderAPIClient.instance
  }

  static resetInstance(): void {
    ProviderAPIClient.instance = null
  }

  public setApiKey(apiKey: string) {
    this.apiKey = apiKey
  }

  private async request(request: RequestStructure, withAuth: boolean = true): Promise<Response> {
    const outgoingHeaders: Record<string, string> = request.headers || {}
    if (withAuth) {
      if (!this.apiKey) {
        throw new Error('API key is not set')
      }
      outgoingHeaders['x-api-key'] = this.apiKey
    }

    // Properly handle request body serialization
    let processedBody: string | undefined
    if (request.body !== undefined && request.body !== null) {
      if (typeof request.body === 'string') {
        processedBody = request.body // Already a string, don't double-stringify
      } else {
        processedBody = JSON.stringify(request.body) // Stringify objects
      }
    }

    const response = await fetch(`${ProviderAPIClient.baseURL}${request.path}`, {
      method: request.method,
      headers: outgoingHeaders,
      body: processedBody,
    })
    return response
  }

  public async get(path: string, headers?: Record<string, string>, withAuth: boolean = true): Promise<Response> {
    return this.request({ method: HTTPMethod.GET, path, headers }, withAuth)
  }

  public async post(
    path: string,
    body?: string | object,
    headers?: Record<string, string>,
    withAuth: boolean = true
  ): Promise<Response> {
    return this.request({ method: HTTPMethod.POST, path, body, headers }, withAuth)
  }

  public async put(
    path: string,
    body?: string | object,
    headers?: Record<string, string>,
    withAuth: boolean = true
  ): Promise<Response> {
    return this.request({ method: HTTPMethod.PUT, path, body, headers }, withAuth)
  }

  public async delete(path: string, headers?: Record<string, string>, withAuth: boolean = true): Promise<Response> {
    return this.request({ method: HTTPMethod.DELETE, path, headers }, withAuth)
  }
}

export default ProviderAPIClient
